import type { FormEvent, RefObject } from "react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { FormSubmitButton } from "~/components/form-fields/FormSubmitButton";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { cn } from "~/shared/lib/utils";
import { AuthPageHeader } from "./auth-page-header";

type MfaMethod = "totp" | "backup";

type LoginTwoFactorStepProps = {
  codeInputRef: RefObject<HTMLInputElement | null>;
  errorMessage: string;
  isLoading: boolean;
  method: MfaMethod;
  onBack: () => void;
  onCodeChange: (value: string) => void;
  onMethodChange: (method: MfaMethod) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  value: string;
};

export function LoginTwoFactorStep({
  codeInputRef,
  errorMessage,
  isLoading,
  method,
  onBack,
  onCodeChange,
  onMethodChange,
  onSubmit,
  value,
}: LoginTwoFactorStepProps) {
  const { i18n } = useLingui();

  return (
    <div className="flex flex-col gap-6">
      <form data-testid="login-2fa-form" onSubmit={onSubmit}>
        <div className="flex flex-col gap-6">
          <AuthPageHeader
            eyebrow={i18n._(msg`Security check`)}
            title={i18n._(msg`Confirm your sign-in`)}
            description={
              method === "backup"
                ? i18n._(msg`Enter one of your backup codes.`)
                : i18n._(msg`Enter the code from your authenticator app.`)
            }
          />

          <div className="flex flex-col gap-4">
            <div className="grid gap-2">
              <Label htmlFor="two-factor-code">
                {method === "backup"
                  ? i18n._(msg`Backup code`)
                  : i18n._(msg`Authentication code`)}
              </Label>
              <SegmentedMethodSwitch
                value={method}
                onChange={onMethodChange}
                disabled={isLoading}
                totpLabel={i18n._(msg`Authenticator`)}
                backupLabel={i18n._(msg`Backup code`)}
                groupLabel={i18n._(msg`Verification method`)}
              />
              <Input
                ref={codeInputRef}
                id="two-factor-code"
                autoComplete="one-time-code"
                inputMode="numeric"
                placeholder={
                  method === "backup" ? i18n._(msg`backup-xxxxxx`) : i18n._(msg`123456`)
                }
                value={value}
                onChange={(event) => onCodeChange(event.target.value.trim())}
                data-testid="login-2fa-code"
              />
            </div>

            <FormSubmitButton
              isSubmitting={isLoading}
              className="mt-2 w-full"
              size="lg"
              loadingText={i18n._(msg`Verifying...`)}
            >
              {i18n._(msg`Verify code`)}
            </FormSubmitButton>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              disabled={isLoading}
              onClick={onBack}
            >
              {i18n._(msg`Back to login`)}
            </Button>
          </div>

          {errorMessage ? (
            <span className="text-destructive text-center text-sm" role="alert">
              {errorMessage}
            </span>
          ) : null}
        </div>
      </form>
    </div>
  );
}

type SegmentedMethodSwitchProps = {
  value: MfaMethod;
  onChange: (method: MfaMethod) => void;
  disabled?: boolean;
  totpLabel: string;
  backupLabel: string;
  groupLabel: string;
};

function SegmentedMethodSwitch({
  value,
  onChange,
  disabled,
  totpLabel,
  backupLabel,
  groupLabel,
}: SegmentedMethodSwitchProps) {
  const options: Array<{ id: MfaMethod; label: string }> = [
    { id: "totp", label: totpLabel },
    { id: "backup", label: backupLabel },
  ];

  return (
    <div
      role="radiogroup"
      aria-label={groupLabel}
      className="bg-muted flex w-full rounded-md p-0.5"
    >
      {options.map((option) => {
        const selected = value === option.id;
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(option.id)}
            className={cn(
              "focus-visible:ring-ring relative flex-1 rounded-[0.3125rem] px-3 py-1.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none",
              selected
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
              disabled && "cursor-not-allowed opacity-50",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
